// ===== FILE: helpers/firebaseAuthLogger.ts ===== (NEW FILE)
// Service to log Firebase authentication events to your backend

interface FirebaseAuthEventData {
    eventType: string;
    email?: string;
    username?: string;
    userId?: string | null;
    success: boolean;
    errorMessage?: string | null;
    provider?: string;
    isNewUser?: boolean;
    
    // Client-side metadata
    clientTimestamp: string;
    timezone: string;
    screenResolution: string;
    windowSize: string;
    colorDepth: number;
    language: string;
    platform: string;
    cookieEnabled: boolean;
    onlineStatus: boolean;
    touchSupport: boolean;
    webGLSupport: boolean;
    localStorageSupport: boolean;
    sessionStorageSupport: boolean;
    referrer: string;
    userAgent: string;
  }
  
  class FirebaseAuthLogger {
    private collectClientData(): Partial<FirebaseAuthEventData> {
      return {
        clientTimestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        colorDepth: window.screen.colorDepth,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onlineStatus: navigator.onLine,
        touchSupport: 'ontouchstart' in window,
        webGLSupport: !!window.WebGLRenderingContext,
        localStorageSupport: typeof(Storage) !== "undefined",
        sessionStorageSupport: typeof(Storage) !== "undefined",
        referrer: document.referrer,
        userAgent: navigator.userAgent,
      };
    }
  
    private async logToBackend(eventData: FirebaseAuthEventData): Promise<boolean> {
      try {
        console.log('🔍 Logging Firebase auth event to backend:', eventData.eventType, eventData.success ? 'SUCCESS' : 'FAILED');
        const serverUrl = process.env.REACT_APP_SERVER;
        const apiUrl = `${serverUrl}/auth/firebase-event-log`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Info': JSON.stringify({
              timestamp: Date.now(),
              userAgent: navigator.userAgent,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
              screen: `${window.screen.width}x${window.screen.height}`,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
          },
          body: JSON.stringify(eventData),
        });
  
        if (response.ok) {
          console.log('✅ Firebase auth event logged to backend successfully');
          return true;
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to log Firebase auth event to backend:', response.status, errorText);
          return false;
        }
      } catch (error) {
        console.error('❌ Error logging Firebase auth event:', error);
        return false;
      }
    }
  
    async logFirebaseEvent(
      eventType: string,
      user: any,
      success: boolean,
      errorMessage?: string,
      provider?: string,
      isNewUser?: boolean
    ): Promise<void> {
      try {
        const clientData = this.collectClientData();
        
        const eventData: FirebaseAuthEventData = {
          eventType,
          email: user?.email || '',
          username: user?.displayName || user?.email?.split('@')[0] || '',
          userId: user?.uid || null,
          success,
          errorMessage: errorMessage || null,
          provider: provider || 'unknown',
          isNewUser: isNewUser || false,
          
          // Explicitly assign client data with defaults
          clientTimestamp: clientData.clientTimestamp || new Date().toISOString(),
          timezone: clientData.timezone || 'unknown',
          screenResolution: clientData.screenResolution || 'unknown',
          windowSize: clientData.windowSize || 'unknown',
          colorDepth: clientData.colorDepth || 0,
          language: clientData.language || 'unknown',
          platform: clientData.platform || 'unknown',
          cookieEnabled: clientData.cookieEnabled || false,
          onlineStatus: clientData.onlineStatus || false,
          touchSupport: clientData.touchSupport || false,
          webGLSupport: clientData.webGLSupport || false,
          localStorageSupport: clientData.localStorageSupport || false,
          sessionStorageSupport: clientData.sessionStorageSupport || false,
          referrer: clientData.referrer || '',
          userAgent: clientData.userAgent || 'unknown',
        };
  
        // Log to backend (non-blocking)
        this.logToBackend(eventData).catch(error => {
          console.error('Failed to log Firebase event to backend:', error);
        });
  
      } catch (error) {
        console.error('Error preparing Firebase auth event log:', error);
      }
    }
  
    // Convenience methods for common events
    async logLogin(user: any, provider: string = 'email'): Promise<void> {
      await this.logFirebaseEvent('firebase_login', user, true, undefined, provider);
    }
  
    async logLoginFailure(email: string, errorMessage: string, provider: string = 'email'): Promise<void> {
      await this.logFirebaseEvent('firebase_login', { email }, false, errorMessage, provider);
    }
  
    async logRegistration(user: any, provider: string = 'email'): Promise<void> {
      await this.logFirebaseEvent('firebase_register', user, true, undefined, provider, true);
    }
  
    async logRegistrationFailure(email: string, errorMessage: string, provider: string = 'email'): Promise<void> {
      await this.logFirebaseEvent('firebase_register', { email }, false, errorMessage, provider);
    }
  
    async logSocialLogin(user: any, provider: string, isNewUser: boolean = false): Promise<void> {
      await this.logFirebaseEvent(`firebase_social_${provider}`, user, true, undefined, provider, isNewUser);
    }
  
    async logSocialLoginFailure(errorMessage: string, provider: string): Promise<void> {
      await this.logFirebaseEvent(`firebase_social_${provider}`, null, false, errorMessage, provider);
    }
  
    async logLogout(user: any): Promise<void> {
      await this.logFirebaseEvent('firebase_logout', user, true);
    }
  
    async logPasswordReset(email: string, success: boolean, errorMessage?: string): Promise<void> {
      await this.logFirebaseEvent('firebase_password_reset', { email }, success, errorMessage);
    }
  }
  
  // Export singleton instance
  export const firebaseAuthLogger = new FirebaseAuthLogger();
  export default firebaseAuthLogger;